import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import classnames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';

import Header from '../../../components/shared/Header/Header';
import OpeningDetailsLoader from '../../../components/shared/SkeletonLoaders/OpeningDetailsLoader/OpeningDetailsLoader';
import { IsMobile } from '../../../helpers/IsMobile';
import { getOpeningDetail } from '../../../redux/actions/browseActions';
import { selectCurrentUser } from '../../../selectors/authSelectors';
import {
  selectJobOpeningDetailsLoading,
  selectLoadingJobs,
  selectOpeningDetails,
  selectOpeningDetailsLoading,
  selectOpeningList,
  selectOpeningListLoading,
} from '../../../selectors/browseSelectors';
import { selectOrganizationLoading } from '../../../selectors/organizationSelectors';
import { OpeningReferralCard } from '../OpeningComponents/OpeningReferralCard/OpeningReferralCard';
import OpeningUnavailable from '../OpeningUnavailable/OpeningUnavailable';

import OpeningContent from './OpeningContent';

import styles from './OpeningDetail.module.scss';

const OpeningDetail = memo(({ isPublic, isJobPreview, fromBrowse = false }) => {
  const { t } = useTranslation('common');
  const isMobile = IsMobile();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = useParams();
  const { jobId } = searchParams;
  const isLoadingOrganization = useSelector(selectOrganizationLoading);
  const isOpeningListLoading = useSelector(selectOpeningListLoading);
  const isLoadingJobs = useSelector(selectLoadingJobs);
  const isOpeningDetailsLoading = useSelector(selectOpeningDetailsLoading);
  const isOpeningJobDetailsLoading = useSelector(selectJobOpeningDetailsLoading);
  const isUnavailable = location.pathname.includes('/jobs/details');
  const isMobileRoute = isUnavailable && isMobile;
  const user = useSelector(selectCurrentUser);
  const openingDetails = useSelector(selectOpeningDetails);
  const openings = useSelector(selectOpeningList);

  const { referral_provider_details, avatar_thumb } = openingDetails;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (jobId) {
      dispatch(getOpeningDetail(jobId, user?.uuid));
    }
  }, [jobId, user?.uuid]);

  if (!isOpeningDetailsLoading && (isEmpty(openingDetails) || !openingDetails.published) && isMobile) {
    return (
      <>
        {isMobileRoute && (
          <div className={styles.headerCont}>
            <Header isPublic={isPublic} title={t('headers.browse')} />
          </div>
        )}
        <OpeningUnavailable />
      </>
    );
  }

  if (!isOpeningListLoading && !openingDetails.published && isOpeningJobDetailsLoading) {
    return (
      <>
        {isUnavailable && (
          <div className={styles.headerCont}>
            <Header isPublic={isPublic} title={t('headers.browse')} />
          </div>
        )}
        <OpeningUnavailable />
      </>
    );
  }

  return (
    <>
      {isMobileRoute && (
        <div className={styles.headerCont}>
          <Header isPublic={isPublic} title={t('headers.browse')} />
        </div>
      )}
      <div className={styles.jobPreviewContainer}>
        {!isEmpty(referral_provider_details) && isJobPreview ? (
          <div className={styles.referralCard}>
            <OpeningReferralCard referralProviderDetails={referral_provider_details} avatarThumb={avatar_thumb} />
          </div>
        ) : null}
        {isOpeningListLoading ||
        isOpeningDetailsLoading ||
        (!isLoadingOrganization && !isPublic) ||
        (fromBrowse &&
          (isOpeningListLoading ||
            isOpeningDetailsLoading ||
            isLoadingJobs ||
            (openings.length === 0 && !isOpeningDetailsLoading && !isEmpty(openingDetails)) ||
            (!isEmpty(openingDetails) && openings.length === 0))) ? (
          <div
            className={classnames({ [styles.loaderContainer]: isJobPreview, [styles.loaderPadding]: !isJobPreview })}
          >
            <OpeningDetailsLoader />
          </div>
        ) : (
          !isEmpty(openingDetails) && (
            <OpeningContent openingDetails={openingDetails} isJobPreview={isJobPreview} isPublic={isPublic} />
          )
        )}
      </div>
    </>
  );
});

export default OpeningDetail;
